import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, timestamp, date, json, jsonb, boolean, unique, index, real, primaryKey } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { isWallClockDocumentCreatedAtInRange, } from "../utils/document-created-at-validation.js";
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const projectDateInputSchema = z.union([
    z.date(),
    z.string().regex(DATE_ONLY_REGEX),
    z.string().datetime(),
]);
export const projects = pgTable("projects", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    organizationId: text("organization_id").references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    projektNummer: text("projektnummer"), // Manuelle Eingabe durch Nutzer
    name: text("name").notNull(),
    street: text("street"),
    houseNo: text("house_no"),
    addressSupplement: text("address_supplement"),
    city: text("city"),
    postalCode: text("postal_code"),
    country: text("country"),
    latitude: real("latitude"),
    longitude: real("longitude"),
    auftraggeber: text("auftraggeber"),
    clientTypeId: text("client_type_id").references(() => clientTypes.id, { onDelete: "set null" }), // Client type reference
    beschreibung: text("beschreibung"),
    managerId: text("manager_id"),
    managerTeamEntryId: text("manager_team_entry_id"),
    managerName: text("manager_name"),
    startDatum: date("startdatum", { mode: "string" }),
    endDatum: date("enddatum", { mode: "string" }),
    budget: integer("budget"),
    prioritaet: text("prioritaet").notNull().default("Mittel"), // Legacy field, kept for backward compatibility
    projectStatusId: text("project_status_id").references(() => projectStatuses.id, { onDelete: "set null" }), // Organization-scoped project status reference
    projectPriorityId: text("project_priority_id").references(() => projectPriorities.id, { onDelete: "set null" }), // Organization-scoped project priority reference
    fortschritt: integer("fortschritt").default(0),
    bildUrl: text("bild_url"),
    projektLogo: text("projekt_logo"), // Logo für "PROJEKT" Label
    bearbeiterLogo: text("bearbeiter_logo"), // Logo für "BEARBEITER" Label
    userProfilbild: text("user_profilbild"), // Profilbild des Benutzers
    teamMitglieder: json("team_mitglieder").$type().default([]),
    // Offline-Funktionen
    offlineDataAvailable: boolean("offline_data_available").default(false), // Projekt für Offline-Nutzung verfügbar machen
    currency: text("currency"), // ISO 4217 currency code for displaying ticket costs. Falls back to organization currency if null.
    isDemo: boolean("is_demo").default(false), // Demo projects are provisioned for onboarding and excluded from limits/analytics
    // Soft delete
    isDeleted: boolean("is_deleted").default(false), // Soft delete flag
    erstelltAm: timestamp("erstellt_am").default(sql `now()`),
}, (table) => {
    return {
        // Speeds up: WHERE user_id = ? ORDER BY erstellt_am DESC
        projectsUserErstelltIdx: index("projects_user_erstellt_idx").on(table.userId, table.erstelltAm),
        // Also useful for other user-scoped lookups
        projectsUserIdIdx: index("projects_user_id_idx").on(table.userId),
        // Optimize getProjects: WHERE organization_id = ? AND is_deleted = false
        projectsOrgIdx: index("projects_org_idx").on(table.organizationId),
    };
});
export const tickets = pgTable("tickets", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    projectId: text("project_id").notNull(),
    ticketNumber: text("ticket_number").notNull(), // z.B. "01", "02", "03" pro Projekt
    title: text("title").notNull(),
    description: text("description").notNull(),
    priority: text("priority"), // Legacy field, kept for backward compatibility during migration
    priorityId: text("priority_id").references(() => priorities.id, { onDelete: "set null" }), // Organization-scoped priority reference
    reporterName: text("reporter_name").notNull(),
    reportedByGroupId: text("reported_by_group_id").references(() => teamGroups.id), // Gruppen-basierter Zugriff über "Gemeldet von"
    assigneeName: text("assignee_name"),
    assignedGroupId: text("assigned_group_id").references(() => teamGroups.id), // Gruppen-Zuweisung
    location: text("location"),
    // Neue Felder
    gewerk: text("gewerk"), // Gewerke (PopUp Menu aus Nutzerliste) - Legacy field, kept for backward compatibility
    gewerkId: text("gewerk_id").references(() => gewerke.id, { onDelete: "set null" }), // Organization-scoped gewerk reference
    type: text("type"), // Typ (Abnahme, Begehung, Mangel, etc.) - Legacy field, kept for backward compatibility
    typeId: text("type_id").references(() => typen.id, { onDelete: "set null" }), // Organization-scoped type reference
    statusId: text("status_id").references(() => status.id, { onDelete: "set null" }), // Organization-scoped status reference
    dueDate: timestamp("due_date"), // Erledigen bis
    extensionDate: timestamp("extension_date"), // Nachfrist
    costs: text("costs"), // Kosten mit € und Tausendertrennzeichen
    progress: integer("progress").default(0), // Fortschritt in %
    images: json("images").$type().default([]),
    voiceNotes: json("voice_notes").$type().default([]), // Pfade zu Sprachnotizen
    aiTranscriptions: json("ai_transcriptions").$type().default([]), // KI-generierte Texte
    planLocation: json("plan_location").$type(), // Pin-Position im Plan
    planPreviewImage: text("plan_preview_image"), // Base64 preview image for calendar display
    planPreviewPinPosition: json("plan_preview_pin_position").$type(), // Pin position within preview image (percentages 0-100)
    image1Url: text("image1_url"),
    image2Url: text("image2_url"),
    image3Url: text("image3_url"),
    image4Url: text("image4_url"),
    // Like-System: Gesamt-Anzahl (für Kompatibilität)
    likes: integer("likes").default(0), // Gesamt-Anzahl der Likes für dieses Ticket
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
    completedAt: timestamp("completed_at"),
    isDemo: boolean("is_demo").default(false), // Demo tickets are seeded onboarding data
    // Soft delete
    isDeleted: boolean("is_deleted").default(false), // Soft delete flag
    // Weather data captured at ticket creation (immutable)
    weatherTemperature: text("weather_temperature"),
    weatherWind: text("weather_wind"),
    weatherConditions: text("weather_conditions"),
    weatherPrecipitation: text("weather_precipitation"),
    weatherLocation: text("weather_location"),
    weatherTimestamp: timestamp("weather_timestamp"),
    // Overtime tracking
    overtimeNeeded: boolean("overtime_needed").default(false),
    overtimeHours: real("overtime_hours"),
    overtimeConfirmed: boolean("overtime_confirmed").default(false),
    overtimeConfirmedAt: timestamp("overtime_confirmed_at"),
    overtimeConfirmedBy: text("overtime_confirmed_by").references(() => users.id, { onDelete: "set null" }),
    overtimeRequestedAt: timestamp("overtime_requested_at"),
    overtimeStartDate: timestamp("overtime_start_date"),
    overtimeDeliveryDate: timestamp("overtime_delivery_date"),
}, (table) => {
    return {
        // Optimize getTicketsByProject: WHERE project_id = ? AND is_deleted IS NOT TRUE ORDER BY created_at DESC
        ticketsProjectIdx: index("tickets_project_idx").on(table.projectId, table.createdAt),
        // Optimize sorting by creation date
        ticketsCreatedAtIdx: index("tickets_created_at_idx").on(table.createdAt),
    };
});
// KI-Bildanalyse für hochgeladene Fotos
export const imageAnalysis = pgTable("image_analysis", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    ticketId: text("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    imagePath: text("image_path").notNull(), // Pfad zum analysierten Bild
    metadataId: varchar("metadata_id").references(() => ticketImageMetadata.id, { onDelete: "set null" }), // Direct link to ticket_image_metadata
    // KI-Analyse-Ergebnisse
    analysisText: text("analysis_text").notNull(), // Vollständiger KI-Analysebericht
    detectedDefects: json("detected_defects").$type().default([]), // Erkannte Mängel
    safetyRisks: json("safety_risks").$type().default([]), // Sicherheitsrisiken
    remainingWork: json("remaining_work").$type().default([]), // Restarbeiten
    qualityIssues: json("quality_issues").$type().default([]), // Qualitätsprobleme
    // Bewertungen
    riskLevel: text("risk_level").notNull(), // 'low', 'medium', 'high', 'critical'
    defectSeverity: text("defect_severity"), // 'minor', 'moderate', 'serious', 'critical'
    priorityRecommendation: text("priority_recommendation"), // 'Niedrig', 'Mittel', 'Hoch', 'Kritisch'
    // Status und Metadaten
    userApproved: boolean("user_approved").notNull().default(false), // User hat Analyse bestätigt
    isVisible: boolean("is_visible").notNull().default(true), // Soll Analyse angezeigt werden?
    aiModel: text("ai_model").notNull().default('gpt-5'), // Verwendetes KI-Modell
    confidence: integer("confidence").notNull().default(75), // Vertrauen der KI (0-100%)
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Individuelle User-Likes für Tickets (1 Like pro User pro Ticket)
export const ticketLikes = pgTable("ticket_likes", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: text("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    // Unique constraint: Ein User kann ein Ticket nur einmal liken
    uniqueUserTicket: unique().on(table.userId, table.ticketId),
}));
// Ticket-Historie für Änderungsverfolgung
export const ticketHistory = pgTable("ticket_history", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: text("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // User der die Änderung gemacht hat
    changeType: text("change_type").notNull(), // 'created', 'updated', 'status_changed', 'assigned', etc.
    fieldName: text("field_name"), // Name des geänderten Feldes (z.B. 'status', 'assigneeName', 'progress')
    oldValue: text("old_value"), // Alter Wert als Text
    newValue: text("new_value"), // Neuer Wert als Text
    description: text("description"), // Benutzerfreundliche Beschreibung der Änderung
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Ticket Image Metadata - stores weather data and metadata for each image added to a ticket
export const ticketImageMetadata = pgTable("ticket_image_metadata", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: varchar("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    imageUrl: text("image_url").notNull(), // URL of the image
    imageIndex: integer("image_index").notNull(), // Index of the image in the images array
    // Weather data captured when photo was added
    weatherTemperature: text("weather_temperature"),
    weatherWind: text("weather_wind"),
    weatherConditions: text("weather_conditions"),
    weatherPrecipitation: text("weather_precipitation"),
    weatherLocation: text("weather_location"),
    weatherTimestamp: timestamp("weather_timestamp"),
    description: text("description"), // User-written or AI-accepted description for the image
    addedBy: varchar("added_by").notNull().references(() => users.id, { onDelete: "cascade" }), // User who added the image
    addedAt: timestamp("added_at").default(sql `now()`),
});
// Chat-Nachrichten für Tickets
export const ticketChatMessages = pgTable("ticket_chat_messages", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: text("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    message: text("message").notNull(),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Per-user read tracking for ticket chat messages
// Note: FK constraints omitted because ticket_chat_messages also lacks them (tickets table missing PK constraint in DB)
export const ticketChatReads = pgTable("ticket_chat_reads", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: text("ticket_id").notNull(),
    userId: text("user_id").notNull(),
    lastReadAt: timestamp("last_read_at").notNull().default(sql `now()`),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    // Unique constraint: One read cursor per user per ticket
    uniqueTicketUser: unique().on(table.ticketId, table.userId),
}));
// KI-Sicherheitswarnungen für wetterabhängige Arbeiten
export const weatherWarnings = pgTable("weather_warnings", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    ticketId: text("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    projectId: text("project_id").notNull(),
    // KI-Analyse-Ergebnisse
    workType: text("work_type").notNull(), // z.B. "Kranarbeiten", "Dacharbeiten"
    isOutdoorWork: boolean("is_outdoor_work").notNull().default(false),
    aiRiskLevel: text("ai_risk_level").notNull(), // 'low', 'medium', 'high'
    weatherFactors: json("weather_factors").$type().default([]), // ['Wind', 'Regen', etc.]
    aiConfidence: integer("ai_confidence").notNull().default(50), // 0-100 %
    // Wetter-Risiko-Bewertung
    currentRiskLevel: text("current_risk_level").notNull(), // 'low', 'medium', 'high'
    riskReasons: json("risk_reasons").$type().default([]), // ['Starker Wind: 55 km/h']
    recommendations: json("recommendations").$type().default([]), // ['Kranarbeiten verschieben']
    smartWarningText: text("smart_warning_text"), // KI-generierte intelligente Warnmeldung
    // Warnungsstatus
    warningActive: boolean("warning_active").notNull().default(false),
    warningLevel: text("warning_level"), // 'info', 'warning', 'critical'
    notificationSent: boolean("notification_sent").notNull().default(false),
    userAcknowledged: boolean("user_acknowledged").notNull().default(false),
    // Metadaten
    lastChecked: timestamp("last_checked").default(sql `now()`),
    nextCheck: timestamp("next_check"),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
export const plans = pgTable("plans", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    projectId: text("project_id").notNull(),
    name: text("name").notNull(),
    fileName: text("file_name").notNull(),
    fileType: text("file_type").notNull(), // jpg, png, pdf
    fileSize: integer("file_size").notNull(),
    fileUrl: text("file_url").notNull(),
    sortOrder: integer("sort_order").default(0), // Für Drag & Drop Reihenfolge
    // PDF-Metadaten für 1:1 Darstellung
    paperFormat: text("paper_format"), // A4, A3, A2, A1, A0, Custom
    widthMM: integer("width_mm"), // Breite in Millimetern (als Integer gespeichert)
    heightMM: integer("height_mm"), // Höhe in Millimetern (als Integer gespeichert)
    dpi: integer("dpi"), // Auflösung
    orientation: text("orientation"), // portrait, landscape
    isDemo: boolean("is_demo").default(false), // Demo plans are seeded onboarding data
    isDeleted: boolean("is_deleted").default(false), // Soft delete flag
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => {
    return {
        // Accelerate lookups by id + user_id (covers WHERE id = ? AND user_id = ?)
        plansIdUserIdx: index("plans_id_user_idx").on(table.id, table.userId),
        // Accelerate common listings by project for a user
        plansUserProjectCreatedIdx: index("plans_user_project_created_idx").on(table.userId, table.projectId, table.createdAt),
    };
});
export const planAnnotations = pgTable("plan_annotations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    planId: varchar("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Creator
    data: json("data").notNull(), // Konva node data
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => {
    return {
        planAnnotationsPlanIdx: index("plan_annotations_plan_idx").on(table.planId),
    };
});
// Documents table with PostgreSQL BLOB storage
export const documents = pgTable("documents", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    projectId: text("project_id"), // Nullable - null for organization-level documents
    organizationId: text("organization_id").references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION - null for project-level documents
    name: text("name").notNull(),
    fileName: text("file_name").notNull(),
    fileType: text("file_type").notNull(), // pdf, docx, xlsx, etc.
    fileSize: integer("file_size").notNull(),
    objectPath: text("object_path").notNull(), // Object Storage path instead of fileData
    fileUrl: text("file_url"), // Base64 data URL for file content
    gewerk: text("gewerk"), // Gewerk-Zuordnung per Dropdown
    sortOrder: integer("sort_order").default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Document-Project Connections junction table (many-to-many)
export const documentProjectConnections = pgTable("document_project_connections", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    documentId: text("document_id").notNull().references(() => documents.id, { onDelete: "cascade" }),
    projectId: text("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => {
    return {
        documentProjectUnique: unique().on(table.documentId, table.projectId),
        documentIdIdx: index("document_project_connections_document_id_idx").on(table.documentId),
        projectIdIdx: index("document_project_connections_project_id_idx").on(table.projectId),
    };
});
// Neue Tabellen für Listen-Management
export const gewerke = pgTable("gewerke", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const typen = pgTable("typen", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const status = pgTable("status", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    color: text("color").default('#6b7280'), // Hex-Farbcode für Status
    semanticType: text("semantic_type").notNull().default('other'), // Semantic categorization: open, in_progress, completed, deferred, other
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const priorities = pgTable("priorities", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    color: text("color").default('#6b7280'), // Hex color code for priority
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const projectStatuses = pgTable("project_statuses", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    semanticType: text("semantic_type"), // Language-agnostic: active, paused, completed, other
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    color: text("color").default('#6b7280'), // Hex color code for status
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const projectPriorities = pgTable("project_priorities", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    color: text("color").default('#6b7280'), // Hex color code for priority
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Client types table (global scope, no organization_id)
export const clientTypes = pgTable("client_types", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Positions table for managing member positions
export const positions = pgTable("positions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }), // ORGANIZATION-ISOLATION
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }), // Optional - kept for audit trail
    name: text("name").notNull(),
    isDefault: boolean("is_default").default(false), // System default records cannot be edited/deleted
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Users table for authentication with hierarchical user system
export const users = pgTable("users", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userCode: text("user_code").notNull().unique(), // Automatisch generierter eindeutiger Code (z.B. MP-010-250002 oder MP-010-250002#1)
    mainUserId: text("main_user_id").references(() => users.id), // NULL für Haupt-User, gefüllt für Sub-User
    username: text("username").notNull(),
    email: text("email").notNull(),
    password_hash: text("password_hash").notNull(),
    accountType: text("account_type").default("Projektsteuerung"), // Projektsteuerung, Team-Mitglied, Beobachter
    // Profile data
    firstName: text("first_name"),
    lastName: text("last_name"),
    displayName: text("display_name"),
    phone: text("phone"),
    mobile: text("mobile"), // Mobilnummer für WhatsApp etc.
    positionId: varchar("position_id").references(() => positions.id, { onDelete: "set null" }), // Organization-scoped position reference
    company: text("company"),
    anrede: text("anrede"), // Salutation (Herr, Frau, etc.)
    street: text("street"),
    houseNumber: text("house_number"), // Hausnummer getrennt von Straße
    postalCode: text("postal_code"),
    city: text("city"),
    country: text("country"), // Land/Country
    referenz: text("referenz"), // Referenz-Text für Admin-Verwaltung
    bio: text("bio"), // User biography or description text
    // Account status
    isActive: boolean("is_active").default(true),
    role: text("role").default("user"), // user, admin, super_admin
    lastLogin: timestamp("last_login"),
    profilePhotoUrl: text("profile_photo_url"), // User profile photo URL
    createdAt: timestamp("created_at").default(sql `now()`),
    // Soft delete fields
    isDeleted: boolean("is_deleted").default(false), // Soft delete flag for user account deletion
    deletedAt: timestamp("deleted_at"), // Set when account is soft-deleted
    // Email verification fields
    emailVerified: boolean("email_verified").default(false), // Email verification status
    emailVerificationToken: text("email_verification_token"), // Token for email verification
    emailVerificationExpiresAt: timestamp("email_verification_expires_at"), // Token expiration timestamp
});
// Admin users table (admin web authentication only)
export const adminUsers = pgTable("admin_users", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userCode: text("user_code").notNull().unique(),
    username: text("username").notNull().unique(),
    email: text("email").notNull().unique(),
    password_hash: text("password_hash").notNull(),
    firstName: text("first_name"),
    lastName: text("last_name"),
    displayName: text("display_name"),
    role: text("role").notNull().default("admin"), // admin, super_admin
    isActive: boolean("is_active").default(true),
    adminPagePermissions: json("admin_page_permissions").$type().default([]),
    lastLogin: timestamp("last_login"),
    createdAt: timestamp("created_at").default(sql `now()`),
    isDeleted: boolean("is_deleted").default(false),
});
// Bug users table (bug web authentication only)
export const bugUsers = pgTable("bug_users", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userCode: text("user_code").notNull().unique(),
    username: text("username").notNull().unique(),
    email: text("email").notNull().unique(),
    password_hash: text("password_hash").notNull(),
    displayName: text("display_name"),
    role: text("role").notNull().default("user"), // user
    isActive: boolean("is_active").default(true),
    emailVerified: boolean("email_verified").default(true),
    lastLogin: timestamp("last_login"),
    createdAt: timestamp("created_at").default(sql `now()`),
    isDeleted: boolean("is_deleted").default(false),
});
// User preferences table for storing user settings
export const userPreferences = pgTable("user_preferences", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // Notifications
    emailNotifications: boolean("email_notifications").default(false),
    pushNotifications: boolean("push_notifications").default(false),
    weeklyReports: boolean("weekly_reports").default(false),
    // WhatsApp settings
    whatsappNotifications: boolean("whatsapp_notifications").default(false),
    whatsappNumber: text("whatsapp_number"),
    // Ticket notification preferences
    ticketEmailScope: text("ticket_email_scope").default("all-projects"),
    ticketNotificationMethod: text("ticket_notification_method").default("daily"),
    ticketNotificationTime: text("ticket_notification_time").default("08:00"),
    ticketDeadlineReminder: text("ticket_deadline_reminder").default("deadline"),
    ticketDeadlineReminderTime: text("ticket_deadline_reminder_time").default("08:00"),
    ticketDeadlineScope: text("ticket_deadline_scope").default("all-tickets"),
    ticketNoSelfChanges: boolean("ticket_no_self_changes").default(false),
    ticketContractorOnly: boolean("ticket_contractor_only").default(false),
    // Privacy
    usageHints: boolean("usage_hints").default(false),
    userExperience: boolean("user_experience").default(false),
    // Data Protection
    dataSharing: boolean("data_sharing").default(false),
    marketing: boolean("marketing").default(false),
    cookies: boolean("cookies").default(false),
    // Date format preference
    dateFormat: text("date_format").default("dd.MM.yyyy"),
    // Snap feed preference
    snapFeedScope: text("snap_feed_scope").default("global"),
    // User preferred UI/notification language
    preferredLanguage: text("preferred_language"),
    // Timestamps
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Sessions table for user authentication
export const sessions = pgTable("sessions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    sessionToken: text("session_token").notNull().unique(),
    expiresAt: timestamp("expires_at").notNull(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// User settings table for storing personal preferences
export const userSettings = pgTable("user_settings", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // Notification preferences
    emailNotifications: boolean("email_notifications").default(false),
    pushNotifications: boolean("push_notifications").default(false),
    weeklyReports: boolean("weekly_reports").default(false),
    usageHints: boolean("usage_hints").default(false),
    userExperience: boolean("user_experience").default(false),
    dataSharing: boolean("data_sharing").default(false),
    marketing: boolean("marketing").default(false),
    cookies: boolean("cookies").default(false),
    twoFactor: boolean("two_factor").default(false),
    audioNotifications: boolean("audio_notifications").default(true),
    noSelfChanges: boolean("no_self_changes").default(true),
    contractorOnly: boolean("contractor_only").default(false),
    // WhatsApp-Einstellungen für User
    whatsappNotifications: boolean("whatsapp_notifications").default(false), // WhatsApp-Benachrichtigungen aktiviert
    whatsappNumber: text("whatsapp_number"), // User WhatsApp-Nummer für Nachunternehmer-Nachrichten
    // Wetter-Einstellungen
    weatherLocationPermission: boolean("weather_location_permission").default(false), // Standortberechtigung für Wetter
    // Timezone and date settings
    timezone: text("timezone").default("Europe/Berlin"),
    // Package/Plan information
    currentPlan: text("current_plan").default("Basic"),
    planExpiry: timestamp("plan_expiry"),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Stripe payment tables for subscription management
export const stripeCustomers = pgTable("stripe_customers", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    stripeCustomerId: text("stripe_customer_id").notNull().unique(),
    email: text("email"),
    name: text("name"),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
export const stripeEvents = pgTable("stripe_events", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    stripeEventId: text("stripe_event_id").notNull().unique(),
    eventType: text("event_type").notNull(),
    organizationId: text("organization_id").references(() => organizations.id),
    payload: json("payload").$type(),
    processed: boolean("processed").default(false),
    processedAt: timestamp("processed_at"),
    error: text("error"),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Admin settings table for system configuration
export const adminSettings = pgTable("admin_settings", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    adminEmail: text("admin_email"), // E-Mail für Admin-Benachrichtigungen
    noreplyEmail: text("noreply_email").default("noreply@planum.de"), // Absender für automatische E-Mails
    emailNotifications: boolean("email_notifications").default(true), // Automatische E-Mails aktiviert
    emailLayoutTemplate: text("email_layout_template"), // Shared HTML layout template for all transactional emails
    // WhatsApp-Benachrichtigungen
    adminMobile: text("admin_mobile"), // WhatsApp-Nummer für Admin (+49 171 1234567)
    whatsappNotifications: boolean("whatsapp_notifications").default(false), // WhatsApp-Ticket-Benachrichtigungen aktiviert
    // SMTP-Server Einstellungen
    smtpHost: text("smtp_host"), // z.B. smtp.gmail.com, mail.strato.de
    smtpPort: integer("smtp_port").default(587), // Standard SMTP Port (587 für TLS, 465 für SSL, 25 für unverschlüsselt)
    smtpSecure: boolean("smtp_secure").default(true), // TLS/SSL aktiviert
    smtpUser: text("smtp_user"), // SMTP Benutzername (oft identisch mit E-Mail)
    smtpPassword: text("smtp_password"), // SMTP Passwort oder App-Passwort
    sessionTimeout: integer("session_timeout").default(30), // Session-Timeout in Minuten
    maxLoginAttempts: integer("max_login_attempts").default(3), // Max. Login-Versuche
    require2FA: boolean("require_2fa").default(false), // 2FA für Admins erforderlich
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Team table
export const teams = pgTable("teams", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    assignedUserCode: text("assigned_user_code"), // Automatisch zugewiesene User-ID (z.B. MP-010-250001#1)
    anrede: text("anrede"), // Herr, Frau
    name: text("name").notNull(),
    lastName: text("last_name"),
    positionId: varchar("position_id").references(() => positions.id, { onDelete: "set null" }), // Organization-scoped position reference
    street: text("street"),
    houseNumber: text("house_number"),
    postalCode: text("postal_code"),
    city: text("city"),
    phone: text("phone"),
    mobile: text("mobile"), // Format: +49 172 1234567 (WhatsApp-kompatibel)
    email: text("email"),
    project: text("project"),
    vacationStart: text("vacation_start"),
    vacationEnd: text("vacation_end"),
    notes: text("notes"),
    isExternal: boolean("is_external").default(false), // Kennzeichnung für externe Team-Mitglieder (Sub-User)
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Team-Gruppen für bessere Organisation
export const teamGroups = pgTable("team_groups", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // USER-ISOLATION
    projectId: text("project_id").references(() => projects.id, { onDelete: "cascade" }), // Project-scoped groups
    name: text("name").notNull(),
    description: text("description"),
    color: text("color").default("#3B82F6"), // Standard-Blau
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Many-to-Many Beziehung zwischen Gruppen und Team-Mitgliedern
export const teamGroupMembers = pgTable("team_group_members", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    groupId: text("group_id").notNull().references(() => teamGroups.id, { onDelete: "cascade" }),
    projectTeamEntryId: text("project_team_entry_id").notNull().references(() => projectTeamEntries.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    // Unique constraint: A project team entry can only exist once per group
    uniqueGroupMember: unique().on(table.groupId, table.projectTeamEntryId),
}));
// Base schema from Drizzle - this will include all fields from the projects table
const baseProjectInsertSchema = createInsertSchema(projects).omit({
    id: true,
    userId: true, // Server fügt userId automatisch hinzu, daher ausschließen
    erstelltAm: true,
});
export const insertProjectSchema = baseProjectInsertSchema.extend({
    projektNummer: z.string().nullable().optional(),
    beschreibung: z.string().nullable().optional(),
    // Address fields - explicitly define to ensure they're preserved
    street: z.string().nullable().optional(),
    houseNo: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    postalCode: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    auftraggeber: z.string().nullable().optional(),
    clientTypeId: z.string().nullable().optional(), // Client type ID reference
    managerId: z.string().nullable().optional(),
    managerTeamEntryId: z.string().nullable().optional(),
    managerName: z.string().nullable().optional(),
    // Project start/end are calendar dates. Accept date-only strings and legacy datetimes.
    startDatum: projectDateInputSchema.nullable().optional(),
    endDatum: projectDateInputSchema.nullable().optional(),
    budget: z.union([z.number(), z.string().transform(val => val ? parseInt(val) : undefined)]).nullable().optional(),
    prioritaet: z.string().default("mittel"), // Legacy field, kept for backward compatibility
    status: z.string().nullable().optional(), // DEPRECATED: Legacy field - do not use. Use projectStatusId instead.
    projectStatusId: z.string().nullable().optional(),
    projectPriorityId: z.string().nullable().optional(),
    fortschritt: z.number().default(0),
    bildUrl: z.string().nullable().optional(),
    teamMitglieder: z.array(z.object({ name: z.string(), position: z.string() })).default([]),
    offlineDataAvailable: z.boolean().default(false).optional(),
    currency: z.string().nullable().optional(),
    isDeleted: z.boolean().default(false).optional(),
}).passthrough(); // Allow unknown fields to pass through (preserves fields not explicitly defined)
// Ticket Assignees - Unified table for anyone who can be assigned to tickets
// Single source of truth for ticket assignment identity (contractors, team members)
export const ticketAssignees = pgTable("ticket_assignees", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: varchar("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    // Linked user (NULL for external/unregistered contacts)
    userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
    invitationId: varchar("invitation_id").references(() => userInvitations.id, { onDelete: "set null" }),
    // External contact info (required when user_id IS NULL)
    externalDisplayName: text("external_display_name"),
    externalPhone: text("external_phone"),
    externalEmail: text("external_email"),
    companyName: text("company_name"),
    // Classification
    assigneeType: text("assignee_type").notNull(), // 'team_member' or 'contractor'
    contractorRole: text("contractor_role"), // 'contractor', 'watcher', 'business_owner'
    // Settings
    notifyViaWhatsapp: boolean("notify_via_whatsapp").notNull().default(true),
    isActive: boolean("is_active").notNull().default(true),
    // Org-specific fields
    vacationStart: text("vacation_start"),
    vacationEnd: text("vacation_end"),
    notes: text("notes"),
    // Deprecated project assignments. Use project_team_entries instead.
    projectIds: jsonb("project_ids").default([]),
    positionId: text("position_id").references(() => positions.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").notNull().default(sql `now()`),
    updatedAt: timestamp("updated_at").notNull().default(sql `now()`),
});
// Ticket Assignments - Links tickets to assignees
export const ticketAssignments = pgTable("ticket_assignments", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: varchar("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    assigneeId: varchar("assignee_id").notNull().references(() => ticketAssignees.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Automatische Zuordnungstabelle: Display-Namen → Sub-User-IDs (beim Anlegen erstellt)
export const userDisplayNames = pgTable("user_display_names", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    mainUserId: varchar("main_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    displayName: text("display_name").notNull(), // "Max Mustermann"
    subUserId: varchar("sub_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Base ticket schema without refine (needed for .partial() to work)
const baseInsertTicketSchema = createInsertSchema(tickets).omit({
    id: true,
    ticketNumber: true, // Wird automatisch generiert
    userId: true, // Wird automatisch aus Session gesetzt
    assigneeName: true, // Deprecated - use assignedContractorId instead
    createdAt: true,
    updatedAt: true,
    completedAt: true,
}).extend({
    description: z.string().default(""),
    reporterName: z.string().default(""),
    reportedByGroupId: z.string().nullable().optional(),
    assignedContractorId: z.string().nullable().optional().describe("DEPRECATED: Use assigneeId instead. Contractor userId to assign ticket to."),
    assigneeId: z.string().nullable().optional().describe("Ticket assignee ID from ticket_assignees table"),
    assignedGroupId: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    gewerk: z.string().nullable().optional(), // Legacy field, kept for backward compatibility
    gewerkId: z.string().nullable().optional().describe("Gewerk ID reference (preferred over gewerk text)"),
    type: z.string().nullable().optional(), // Legacy field, kept for backward compatibility
    typeId: z.string().nullable().optional().describe("Type ID reference (preferred over type text)"),
    statusId: z.string().nullable().optional().describe("Status ID reference to organization status table"),
    dueDate: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    extensionDate: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    costs: z.string().nullable().optional(),
    progress: z.number().min(0).max(100).nullable().optional(),
    planLocation: z.object({
        x: z.number(),
        y: z.number(),
        planId: z.string(),
        zoom: z.number()
    }).nullable().optional(),
    planPreviewImage: z.string().nullable().optional(),
    planPreviewPinPosition: z.object({
        x: z.number(),
        y: z.number()
    }).nullable().optional(),
    images: z.array(z.string()).max(10).optional().default([]),
    image1Url: z.string().nullable().optional(),
    image2Url: z.string().nullable().optional(),
    image3Url: z.string().nullable().optional(),
    image4Url: z.string().nullable().optional(),
    // Weather data (optional, captured at ticket creation)
    weatherTemperature: z.string().nullable().optional(),
    weatherWind: z.string().nullable().optional(),
    weatherConditions: z.string().nullable().optional(),
    weatherPrecipitation: z.string().nullable().optional(),
    weatherLocation: z.string().nullable().optional(),
    weatherTimestamp: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (!val)
            return null;
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    // Overtime fields
    overtimeNeeded: z.boolean().optional().default(false),
    overtimeHours: z.number().min(1).max(24).multipleOf(0.5).nullable().optional(),
    overtimeConfirmed: z.boolean().optional().default(false),
    overtimeConfirmedAt: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (!val)
            return null;
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    overtimeConfirmedBy: z.string().nullable().optional(),
    overtimeRequestedAt: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (!val)
            return null;
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    overtimeStartDate: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (!val)
            return null;
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    overtimeDeliveryDate: z.union([z.date(), z.string().datetime()]).nullable().optional().transform((val) => {
        if (!val)
            return null;
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
});
// Partielles Update-Schema für Tickets (für PATCH-Requests) - must be created before refine
export const updateTicketSchema = baseInsertTicketSchema
    .partial()
    .refine((data) => {
    // If overtimeNeeded is true, overtimeHours must be provided
    if (data.overtimeNeeded === true) {
        return data.overtimeHours !== null && data.overtimeHours !== undefined;
    }
    return true;
}, {
    message: "Overtime hours are required when overtime is needed",
    path: ["overtimeHours"],
})
    .refine((data) => {
    if (!data.overtimeStartDate || !data.overtimeDeliveryDate)
        return true;
    return new Date(data.overtimeStartDate).getTime() <= new Date(data.overtimeDeliveryDate).getTime();
}, {
    message: "Overtime delivery date must be after or equal to overtime start date",
    path: ["overtimeDeliveryDate"],
});
export { TICKET_CREATED_AT_MAX_FUTURE_MS, TICKET_CREATED_AT_MAX_PAST_MS, isWallClockDocumentCreatedAtInRange, } from "../utils/document-created-at-validation.js";
// Insert schema with refine validation
export const insertTicketSchema = baseInsertTicketSchema
    .extend({
    /** Optional document wall-clock time (encoded as UTC components); validated in refine. */
    createdAt: z
        .union([
        z.string(),
        z.date(),
    ])
        .optional()
        .transform((val) => {
        if (val === undefined || val === null)
            return undefined;
        const d = val instanceof Date ? val : new Date(val);
        return Number.isNaN(d.getTime()) ? undefined : d;
    }),
})
    .refine((data) => {
    // If overtimeNeeded is true, overtimeHours must be provided
    if (data.overtimeNeeded === true) {
        return data.overtimeHours !== null && data.overtimeHours !== undefined;
    }
    return true;
}, {
    message: "Overtime hours are required when overtime is needed",
    path: ["overtimeHours"],
})
    .refine((data) => {
    if (!data.overtimeStartDate || !data.overtimeDeliveryDate)
        return true;
    return new Date(data.overtimeStartDate).getTime() <= new Date(data.overtimeDeliveryDate).getTime();
}, {
    message: "Overtime delivery date must be after or equal to overtime start date",
    path: ["overtimeDeliveryDate"],
})
    .refine((data) => {
    const c = data.createdAt;
    if (c == null || !(c instanceof Date) || Number.isNaN(c.getTime()))
        return true;
    return isWallClockDocumentCreatedAtInRange(c);
}, {
    message: "TICKET_CREATED_AT_OUT_OF_RANGE",
    path: ["createdAt"],
});
export const insertPlanSchema = createInsertSchema(plans).omit({
    id: true,
    userId: true, // Server fügt userId automatisch hinzu
    createdAt: true,
}).extend({
    // PDF-Metadaten explizit validieren
    paperFormat: z.string().nullable().optional(),
    widthMM: z.number().nullable().optional(),
    heightMM: z.number().nullable().optional(),
    dpi: z.number().nullable().optional(),
    orientation: z.string().nullable().optional(),
    // Basis-Felder validieren
    projectId: z.string().min(1, "Projekt-ID ist erforderlich"),
    name: z.string().min(1, "Name ist erforderlich"),
    fileName: z.string().min(1, "Dateiname ist erforderlich"),
    fileType: z.string().min(1, "Dateityp ist erforderlich"),
    fileSize: z.number().min(1, "Dateigröße ist erforderlich"),
    fileUrl: z.string().min(1, "Datei-URL ist erforderlich"),
    sortOrder: z.number().default(0),
});
export const insertPlanAnnotationSchema = createInsertSchema(planAnnotations).omit({
    id: true,
    userId: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    planId: z.string().min(1, "Plan ID is required"),
    data: z.any(), // Validate structure if possible, but Konva data can be complex
});
export const insertDocumentSchema = createInsertSchema(documents).omit({
    id: true,
    userId: true, // Server fügt userId automatisch hinzu, daher ausschließen
    createdAt: true,
}).extend({
    projectId: z.string().min(1, "Projekt-ID ist erforderlich"),
    name: z.string().min(1, "Name ist erforderlich"),
    fileName: z.string().min(1, "Dateiname ist erforderlich"),
    fileType: z.string().min(1, "Dateityp ist erforderlich"),
    fileSize: z.number().min(1, "Dateigröße ist erforderlich"),
    objectPath: z.string().min(1, "Object-Path ist erforderlich"),
    sortOrder: z.number().default(1),
});
export const insertGewerkSchema = createInsertSchema(gewerke).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    organizationId: z.string().min(1, "Organization ID is required"),
});
export const insertTypSchema = createInsertSchema(typen).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    organizationId: z.string().min(1, "Organization ID is required"),
});
export const insertStatusSchema = createInsertSchema(status).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    organizationId: z.string().min(1, "Organization ID is required"),
});
export const insertTeamSchema = createInsertSchema(teams).omit({
    id: true,
    userId: true, // Server fügt userId automatisch hinzu, daher ausschließen
    assignedUserCode: true, // Server generiert automatisch
    createdAt: true,
}).extend({
    anrede: z.string().nullable().optional(),
    name: z.string().min(1, "Name ist erforderlich"),
    lastName: z.string().nullable().optional(),
    position: z.string().default(""), // Default-Wert für Required-Field
    street: z.string().nullable().optional(),
    houseNumber: z.string().nullable().optional(),
    postalCode: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    mobile: z.string().nullable().optional(),
    email: z.string().optional().refine(val => !val || val === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
        message: "Ungültige E-Mail-Adresse"
    }),
    project: z.string().nullable().optional(),
    vacationStart: z.string().nullable().optional(),
    vacationEnd: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    isExternal: z.boolean().default(false).optional(), // Kennzeichnung für externe Team-Mitglieder
});
export const insertPrioritySchema = createInsertSchema(priorities).omit({
    id: true,
    organizationId: true,
    userId: true,
    createdAt: true,
});
export const insertProjectStatusSchema = createInsertSchema(projectStatuses).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    organizationId: z.string().min(1, "Organization ID is required"),
});
export const insertProjectPrioritySchema = createInsertSchema(projectPriorities).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    organizationId: z.string().min(1, "Organization ID is required"),
});
export const insertUserSchema = createInsertSchema(users).omit({
    id: true,
    createdAt: true,
});
export const insertAdminUserSchema = createInsertSchema(adminUsers).omit({
    id: true,
    createdAt: true,
});
export const insertBugUserSchema = createInsertSchema(bugUsers).omit({
    id: true,
    createdAt: true,
});
export const insertUserPreferencesSchema = createInsertSchema(userPreferences).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
// Gruppen-Schemas
export const insertTeamGroupSchema = createInsertSchema(teamGroups).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    description: z.string().nullable().optional(),
    color: z.string().default("#3B82F6"),
});
export const insertPositionSchema = createInsertSchema(positions).omit({
    id: true,
    userId: true,
    createdAt: true,
}).extend({
    organizationId: z.string().min(1, "Organization ID is required"),
});
export const insertUserSettingsSchema = createInsertSchema(userSettings).omit({
    id: true,
    updatedAt: true,
});
export const insertAdminSettingsSchema = createInsertSchema(adminSettings).omit({
    id: true,
    updatedAt: true,
});
// Organizations table for enterprise accounts
export const organizations = pgTable("organizations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull(),
    website: text("website"),
    industry: text("industry"),
    size: text("size"), // small, medium, large, enterprise
    address: text("address"), // Legacy single address field (deprecated, use structured fields below)
    street: text("street"),
    houseNumber: text("house_number"),
    postalCode: text("postal_code"),
    city: text("city"),
    country: text("country"),
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),
    adminUserId: text("admin_user_id").references(() => users.id),
    subscriptionType: text("subscription_type").default("enterprise"),
    maxUsers: integer("max_users").default(10),
    currentUsers: integer("current_users").default(0),
    isActive: boolean("is_active").default(true),
    language: text("language").default('en'),
    currency: text("currency").default('EUR'), // ISO 4217 currency code for ticket costs
    logoUrl: text("logo_url"), // Company logo URL
    hideDemoProjects: boolean("hide_demo_projects").default(false), // Org-wide toggle to hide demo projects from listings
    demoProvisioningStatus: text("demo_provisioning_status").default('not_started'), // not_started, pending, ready, failed
    demoProvisioningError: text("demo_provisioning_error"), // last provisioning error for support visibility
    createdAt: timestamp("created_at").default(sql `now()`),
});
// System defaults tables (master source for read-only defaults)
export const systemDefaultStatuses = pgTable("system_default_statuses", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    color: text("color").notNull().default('#6b7280'),
    displayOrder: integer("display_order").notNull().default(0),
    semanticType: text("semantic_type").notNull().default('other'), // Semantic categorization: open, in_progress, completed, deferred, other
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const systemDefaultGewerke = pgTable("system_default_gewerke", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const systemDefaultTypen = pgTable("system_default_typen", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const systemDefaultPositions = pgTable("system_default_positions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const systemDefaultPriorities = pgTable("system_default_priorities", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    color: text("color").notNull().default('#6b7280'),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const systemDefaultProjectStatuses = pgTable("system_default_project_statuses", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    color: text("color").notNull().default('#6b7280'),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
export const systemDefaultProjectPriorities = pgTable("system_default_project_priorities", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    name: text("name").notNull().unique(),
    color: text("color").notNull().default('#6b7280'),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Organization Roles
export const organizationRoles = pgTable("organization_roles", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    isSystem: boolean("is_system").notNull().default(false),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    uniqueOrgRoleName: unique().on(table.organizationId, table.name),
}));
// Organization Members (Employees only - subject to subscription limits)
export const organizationMembers = pgTable("organization_members", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    roleId: text("role_id").notNull().references(() => organizationRoles.id, { onDelete: "restrict" }),
    status: text("status").notNull().default("active"), // active, invited, suspended
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    uniqueOrgUser: unique().on(table.organizationId, table.userId),
}));
/**
 * @deprecated This table has been dropped as part of the Unified Ticket Assignees Refactor.
 * All contractor functionality has been consolidated into the `ticket_assignees` table.
 * This schema definition is kept for backward compatibility during the transition period only.
 * See migration 0143_drop_organization_contractors.sql
 */
export const organizationContractors = pgTable("organization_contractors", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("contractor"), // contractor, watcher, business_owner
    status: text("status").notNull().default("active"), // active, invited, suspended
    vacationStart: text("vacation_start"),
    vacationEnd: text("vacation_end"),
    notes: text("notes"),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    uniqueOrgContractor: unique().on(table.organizationId, table.userId),
}));
// Organization Invitations
export const organizationInvitations = pgTable("organization_invitations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    role: text("role").notNull().default("admin"),
    token: text("token").notNull().unique(),
    expiresAt: timestamp("expires_at").notNull(),
    acceptedAt: timestamp("accepted_at"),
    createdAt: timestamp("created_at").default(sql `now()`),
    status: text("status").notNull().default("sent"), // pending, sent, accepted, rejected, expired
    invitedBy: text("invited_by").references(() => users.id),
    userId: text("user_id").references(() => users.id), // set when invitee is an existing user
    firstName: text("first_name"),
    lastName: text("last_name"),
    mobile: text("mobile"),
    anrede: text("anrede"),
});
// Project Members (many-to-many: projects <-> users)
export const projectMembers = pgTable("project_members", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    projectId: text("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("member"), // member, viewer, manager
    assignedAt: timestamp("assigned_at").default(sql `now()`),
}, (table) => ({
    uniqueProjectUser: unique().on(table.projectId, table.userId),
}));
export const projectTeamEntries = pgTable("project_team_entries", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    projectId: text("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
    assigneeId: varchar("assignee_id").references(() => ticketAssignees.id, { onDelete: "cascade" }),
    invitationId: varchar("invitation_id").references(() => userInvitations.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("member"),
    assignedAt: timestamp("assigned_at").default(sql `now()`),
}, (table) => ({
    projectTeamProjectIdx: index("idx_project_team_entries_project_id").on(table.projectId),
    projectTeamAssigneeIdx: index("idx_project_team_entries_assignee_id").on(table.assigneeId),
    projectTeamInvitationIdx: index("idx_project_team_entries_invitation_id").on(table.invitationId),
}));
// User Invitations (track pending invitations for new users and existing users)
export const userInvitations = pgTable("user_invitations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    email: text("email").notNull(),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    projectId: text("project_id").references(() => projects.id, { onDelete: "cascade" }), // Optional: invite to specific project
    invitedBy: text("invited_by").notNull().references(() => users.id),
    role: text("role").notNull().default("member"),
    firstName: text("first_name"), // First name of contractor (stored before they become a user)
    lastName: text("last_name"), // Last name of contractor (stored before they become a user)
    token: text("token").unique(), // Nullable - created when INVITE button is clicked
    status: text("status").notNull().default("pending"), // pending, accepted, expired, sent, rejected
    expiresAt: timestamp("expires_at"), // Nullable - set when invitation is sent
    acceptedAt: timestamp("accepted_at"),
    createdAt: timestamp("created_at").default(sql `now()`),
    userId: text("user_id").references(() => users.id), // set when invitee is an existing user
});
// Invoice Templates table for admin-controlled invoice design
export const invoiceTemplates = pgTable("invoice_templates", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    // Company Header Section
    companyName: text("company_name").default("FOLW GmbH"),
    companyAddress: text("company_address").default(""),
    companyCity: text("company_city").default(""),
    companyPhone: text("company_phone").default(""),
    companyEmail: text("company_email").default(""),
    companyWebsite: text("company_website").default(""),
    companyLogo: text("company_logo").default(""), // URL to logo
    // Invoice Header Section
    invoiceTitle: text("invoice_title").default("Rechnung"),
    invoiceNumberPrefix: text("invoice_number_prefix").default("RE"),
    // Footer Section
    footerText: text("footer_text").default(""),
    bankDetails: text("bank_details").default(""),
    taxNumber: text("tax_number").default(""),
    vatId: text("vat_id").default(""),
    ceoName: text("ceo_name").default(""),
    registrationCourt: text("registration_court").default(""),
    // Styling Options
    primaryColor: text("primary_color").default("#1E293B"),
    secondaryColor: text("secondary_color").default("#64748B"),
    fontFamily: text("font_family").default("Helvetica"),
    fontSize: integer("font_size").default(12),
    // Template Layout Structure (JSON)
    layout: json("layout").$type().default({
        headerPosition: "top-right",
        logoPosition: "top-left",
        customerPosition: "left",
        itemsTableColumns: ["pos", "menge", "artNr", "bezeichnung", "mwst", "einzelpreis", "gesamtpreis"],
        footerPosition: "bottom"
    }),
    // Dynamic Variables Available
    availableVariables: json("available_variables").$type().default([
        "{{userCode}}", "{{kundenName}}", "{{kundenAdresse}}", "{{rechnungsNummer}}",
        "{{rechnungsDatum}}", "{{leistungsZeitraum}}", "{{gesamtBetrag}}",
        "{{mwstBetrag}}", "{{nettoBetrag}}", "{{zahlungsZiel}}"
    ]),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Subscriptions table for detailed billing history
export const subscriptions = pgTable("subscriptions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").references(() => users.id, { onDelete: "cascade" }),
    organizationId: text("organization_id").references(() => organizations.id),
    subscriptionType: text("subscription_type").notNull(), // free, basic, pro, enterprise
    status: text("status").notNull(), // active, suspended, expired, cancelled, pending
    startDate: timestamp("start_date").notNull(),
    endDate: timestamp("end_date"),
    price: integer("price").default(0), // in cents
    currency: text("currency").default("EUR"),
    paymentMethod: text("payment_method"), // credit_card, bank_transfer, invoice
    billingCycle: text("billing_cycle").default("monthly"), // monthly, yearly
    autoRenew: boolean("auto_renew").default(true),
    features: json("features").$type().default([]),
    maxProjects: integer("max_projects").default(5),
    maxUsers: integer("max_users").default(1),
    storageLimit: integer("storage_limit").default(1000), // in MB
    lastPayment: timestamp("last_payment"),
    nextPayment: timestamp("next_payment"),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Billing history table
export const billingHistory = pgTable("billing_history", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    subscriptionId: text("subscription_id").notNull().references(() => subscriptions.id),
    userId: text("user_id").references(() => users.id),
    organizationId: text("organization_id").references(() => organizations.id),
    invoiceNumber: text("invoice_number").notNull(),
    amount: integer("amount").notNull(), // in cents
    currency: text("currency").default("EUR"),
    status: text("status").notNull(), // paid, pending, failed, refunded
    paymentMethod: text("payment_method"),
    transactionId: text("transaction_id"),
    billingPeriodStart: timestamp("billing_period_start").notNull(),
    billingPeriodEnd: timestamp("billing_period_end").notNull(),
    dueDate: timestamp("due_date").notNull(),
    paidDate: timestamp("paid_date"),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// User activity log for admin monitoring - REMOVED (not implemented)
// export const userActivityLog = pgTable("user_activity_log", {
//   id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
//   userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
//   action: text("action").notNull(), // login, logout, create_project, delete_ticket, etc.
//   resourceType: text("resource_type"), // project, ticket, document, user
//   resourceId: text("resource_id"),
//   details: json("details").$type<Record<string, any>>(),
//   ipAddress: text("ip_address"),
//   userAgent: text("user_agent"),
//   timestamp: timestamp("timestamp").default(sql`now()`),
// });
// Insert schemas for admin tables
export const insertOrganizationSchema = createInsertSchema(organizations).omit({
    id: true,
    createdAt: true,
});
export const insertSubscriptionSchema = createInsertSchema(subscriptions).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertBillingHistorySchema = createInsertSchema(billingHistory).omit({
    id: true,
    createdAt: true,
});
// export const insertUserActivityLogSchema = createInsertSchema(userActivityLog).omit({
//   id: true,
//   timestamp: true,
// });
// Subscription Plans table
export const subscriptionPlans = pgTable("subscription_plans", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    planName: text("plan_name").notNull().unique(), // 'Free', 'Expert', 'Pro', 'Rise'
    currency: text("currency").default("EUR"),
    trialDays: integer("trial_days").default(14), // configurable trial period
    stripePriceIdMonthly: text("stripe_price_id_monthly"),
    stripePriceIdYearly: text("stripe_price_id_yearly"),
    appleProductIdMonthly: text("apple_product_id_monthly"),
    appleProductIdQuarterly: text("apple_product_id_quarterly"),
    googleProductIdMonthly: text("google_product_id_monthly"),
    googleProductIdQuarterly: text("google_product_id_quarterly"),
    billingInterval: text("billing_interval").default("monthly"), // monthly/yearly
    description: text("description"), // plan description
    features: json("features").$type().default([]), // list of plan features
    isActive: boolean("is_active").default(true),
    // Per-seat pricing columns (P0)
    pricePerSeatMonthly: integer("price_per_seat_monthly").default(0), // monthly price per seat in cents
    pricePerSeatYearly: integer("price_per_seat_yearly").default(0), // yearly price per seat in cents
    includedSeats: integer("included_seats").default(1), // number of seats included in base price
    maxSeats: integer("max_seats"), // maximum seats allowed (NULL = unlimited)
    minSeats: integer("min_seats").default(1), // minimum seats required
    maxProjectsPerSeat: integer("max_projects_per_seat"), // maximum projects per seat
    maxPdfPlansPerSeat: integer("max_pdf_plans_per_seat"), // maximum PDF plans per seat
    maxActiveTickets: integer("max_active_tickets"), // maximum active (non-deleted) tickets per organization
    storagePerSeatMb: integer("storage_per_seat_mb").default(500), // storage per seat in MB
    isPerSeat: boolean("is_per_seat").default(true), // all plans are seat-based
    freeContractors: boolean("free_contractors").default(true), // whether contractors count against seat limits
    displayOrder: integer("display_order").default(0), // order for displaying plans in UI
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Subscription plan feature catalog (one row per feature key)
export const subscriptionPlanFeatures = pgTable("subscription_plan_features", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    featureKey: text("feature_key").notNull().unique(),
    isSystem: boolean("is_system").notNull().default(false),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
// Subscription plan feature translations (en/de/tr labels per feature)
export const subscriptionPlanFeatureTranslations = pgTable("subscription_plan_feature_translations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    featureId: varchar("feature_id").notNull().references(() => subscriptionPlanFeatures.id, { onDelete: "cascade" }),
    language: text("language").notNull(), // 'en', 'de', 'tr'
    label: text("label").notNull(),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    featureLanguageUnique: unique("subscription_plan_feature_translations_feature_language").on(table.featureId, table.language),
    featureIdIdx: index("subscription_plan_feature_translations_feature_id_idx").on(table.featureId),
}));
// Per-plan feature assignment (checkbox state)
export const subscriptionPlanFeatureAssignments = pgTable("subscription_plan_feature_assignments", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    planId: varchar("plan_id").notNull().references(() => subscriptionPlans.id, { onDelete: "cascade" }),
    featureId: varchar("feature_id").notNull().references(() => subscriptionPlanFeatures.id, { onDelete: "cascade" }),
    isEnabled: boolean("is_enabled").notNull().default(true),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    planFeatureUnique: unique("subscription_plan_feature_assignments_plan_feature").on(table.planId, table.featureId),
    planIdIdx: index("subscription_plan_feature_assignments_plan_id_idx").on(table.planId),
    featureIdIdx: index("subscription_plan_feature_assignments_feature_id_idx").on(table.featureId),
}));
// Organization Subscriptions table
export const organizationSubscriptions = pgTable("organization_subscriptions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    subscriptionPlanId: text("subscription_plan_id").notNull().references(() => subscriptionPlans.id),
    stripeCustomerId: text("stripe_customer_id"), // nullable, for future Stripe
    stripeSubscriptionId: text("stripe_subscription_id"), // nullable, for future Stripe
    provider: text("provider"),
    providerEnvironment: text("provider_environment"),
    providerProductId: text("provider_product_id"),
    status: text("status").notNull().default("active"), // active, past_due, cancelled, expired, refunded
    trialEndsAt: timestamp("trial_ends_at"), // when trial ends
    currentPeriodStart: timestamp("current_period_start"), // current billing period start
    currentPeriodEnd: timestamp("current_period_end"), // current billing period end
    cancelAtPeriodEnd: boolean("cancel_at_period_end").default(false), // cancel at period end
    // Seat tracking (P0)
    seatCount: integer("seat_count").default(1), // number of seats purchased
    stripeQuantity: integer("stripe_quantity").default(1), // quantity reported to Stripe
    billingInterval: text("billing_interval").default("monthly"), // monthly or yearly
    lastVerifiedAt: timestamp("last_verified_at"),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => {
    return {
        // Ensure one active subscription per organization
        organizationSubscriptionIdx: unique("organization_subscription_idx").on(table.organizationId),
        // Index for Stripe lookups
        stripeSubscriptionIdx: index("stripe_subscription_idx").on(table.stripeSubscriptionId),
    };
});
export const subscriptionProviderRecords = pgTable("subscription_provider_records", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    organizationId: text("organization_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
    subscriptionPlanId: text("subscription_plan_id").notNull().references(() => subscriptionPlans.id),
    provider: text("provider").notNull(),
    providerEnvironment: text("provider_environment").notNull(),
    providerProductId: text("provider_product_id").notNull(),
    providerSubscriptionRef: text("provider_subscription_ref").notNull(),
    providerTransactionId: text("provider_transaction_id"),
    originalProviderRef: text("original_provider_ref"),
    billingInterval: text("billing_interval").notNull().default("monthly"),
    seatCount: integer("seat_count").notNull().default(1),
    status: text("status").notNull(),
    currentPeriodStart: timestamp("current_period_start"),
    currentPeriodEnd: timestamp("current_period_end"),
    rawPayload: json("raw_payload").$type(),
    lastVerifiedAt: timestamp("last_verified_at"),
    acknowledgedAt: timestamp("acknowledged_at"),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    providerSubscriptionRefUnique: unique("subscription_provider_records_provider_ref_unique").on(table.provider, table.providerSubscriptionRef),
    providerTransactionIdIdx: index("subscription_provider_records_provider_transaction_id_idx").on(table.providerTransactionId),
    organizationIdIdx: index("subscription_provider_records_organization_id_idx").on(table.organizationId),
    originalProviderRefIdx: index("subscription_provider_records_original_provider_ref_idx").on(table.originalProviderRef),
}));
export const subscriptionProviderEvents = pgTable("subscription_provider_events", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    provider: text("provider").notNull(),
    providerEventId: text("provider_event_id").notNull(),
    organizationId: text("organization_id").references(() => organizations.id, { onDelete: "set null" }),
    providerEnvironment: text("provider_environment"),
    eventType: text("event_type").notNull(),
    payload: json("payload").$type(),
    processed: boolean("processed").default(false),
    processedAt: timestamp("processed_at"),
    error: text("error"),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    providerEventUnique: unique("subscription_provider_events_provider_event_unique").on(table.provider, table.providerEventId),
    organizationIdIdx: index("subscription_provider_events_organization_id_idx").on(table.organizationId),
}));
// Subscription Settings table (P0)
export const subscriptionSettings = pgTable("subscription_settings", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    settingKey: text("setting_key").notNull().unique(),
    settingValue: text("setting_value").notNull(),
    settingType: text("setting_type").notNull().default("string"), // string, number, boolean, json
    description: text("description"),
    updatedAt: timestamp("updated_at").default(sql `now()`),
    updatedBy: text("updated_by").references(() => users.id),
    updatedByActorType: text("updated_by_actor_type"), // app | admin | bug
    updatedByActorId: text("updated_by_actor_id"),
}, (table) => {
    return {
        settingKeyIdx: index("subscription_settings_key_idx").on(table.settingKey),
    };
});
// User Positions table
export const userPositions = pgTable("user_positions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    positionName: text("position_name").notNull().unique(),
    displayOrder: integer("display_order").notNull().default(0),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").default(sql `now()`),
});
// Insert schemas for new subscription tables
export const insertOrganizationSubscriptionSchema = createInsertSchema(organizationSubscriptions).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertStripeCustomerSchema = createInsertSchema(stripeCustomers).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertStripeEventSchema = createInsertSchema(stripeEvents).omit({
    id: true,
    createdAt: true,
});
export const insertSubscriptionSettingsSchema = createInsertSchema(subscriptionSettings).omit({
    id: true,
    updatedAt: true,
});
export const insertSubscriptionProviderRecordSchema = createInsertSchema(subscriptionProviderRecords).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertSubscriptionProviderEventSchema = createInsertSchema(subscriptionProviderEvents).omit({
    id: true,
    createdAt: true,
});
// User Position Translations table
export const userPositionTranslations = pgTable("user_position_translations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    positionId: varchar("position_id").notNull().references(() => userPositions.id, { onDelete: "cascade" }),
    language: text("language").notNull(), // 'en', 'de', 'tr'
    translatedName: text("translated_name").notNull(),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => {
    return {
        // Ensure unique combination of position and language
        positionLanguageIdx: unique("position_language_idx").on(table.positionId, table.language),
    };
});
// System Default Positions Translations table (for global defaults)
export const systemDefaultPositionsTranslations = pgTable("system_default_positions_translations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    positionId: varchar("position_id").notNull().references(() => systemDefaultPositions.id, { onDelete: "cascade" }),
    language: text("language").notNull(), // 'en', 'de', 'tr'
    translatedName: text("translated_name").notNull(),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => {
    return {
        // Ensure unique combination of position and language
        positionLanguageUnique: unique("system_default_positions_translations_position_language").on(table.positionId, table.language),
    };
});
// Positions Translations table (for organization-scoped positions)
export const positionsTranslations = pgTable("positions_translations", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    positionId: varchar("position_id").notNull().references(() => positions.id, { onDelete: "cascade" }),
    language: text("language").notNull(), // 'en', 'de', 'tr'
    translatedName: text("translated_name").notNull(),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => {
    return {
        // Ensure unique combination of position and language
        positionLanguageUnique: unique("positions_translations_position_language").on(table.positionId, table.language),
    };
});
// Support Messages table
export const supportMessages = pgTable("support_messages", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    ticketId: text("ticket_id").notNull(), // NA-202500001, NA-202500002, etc.
    subject: text("subject").notNull(),
    message: text("message").notNull(),
    folder: text("folder").notNull().default("inbox"), // inbox, outbox, trash, deleted, custom folders
    isFromUser: boolean("is_from_user").notNull().default(true),
    isRead: boolean("is_read").notNull().default(false),
    isPinned: boolean("is_pinned").notNull().default(false),
    isMarked: boolean("is_marked").notNull().default(false),
    parentMessageId: text("parent_message_id"),
    adminResponse: text("admin_response"),
    adminId: text("admin_user_id").references(() => users.id, { onDelete: "set null" }),
    adminActorType: text("admin_actor_type"), // app | admin | bug
    adminActorId: text("admin_actor_id"),
    createdAt: timestamp("created_at").default(sql `now()`).notNull(),
    updatedAt: timestamp("updated_at").default(sql `now()`).notNull()
});
export const insertSupportMessageSchema = createInsertSchema(supportMessages).omit({
    id: true,
    userId: true,
    ticketId: true, // wird automatisch generiert
    createdAt: true,
    updatedAt: true
});
// Notifications table (stores all email/push notifications)
export const notifications = pgTable("notifications", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    organizationId: text("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
    projectId: text("project_id").references(() => projects.id, { onDelete: "cascade" }),
    ticketId: text("ticket_id").references(() => tickets.id, { onDelete: "cascade" }),
    type: text("type").notNull(), // e.g. ticket_created, project_created
    category: text("category").notNull(), // ticket, project, team, subscription, system
    title: text("title").notNull(),
    message: text("message").notNull(),
    data: json("data").$type(),
    emailSent: boolean("email_sent").default(false),
    pushSent: boolean("push_sent").default(false),
    emailSentAt: timestamp("email_sent_at"),
    pushSentAt: timestamp("push_sent_at"),
    pushDeliveredAt: timestamp("push_delivered_at"),
    pushOpenedAt: timestamp("push_opened_at"),
    priority: text("priority").default("medium"), // low, medium, high
    actionUrl: text("action_url"),
    iconUrl: text("icon_url"),
    createdAt: timestamp("created_at").default(sql `now()`),
    expiresAt: timestamp("expires_at"),
});
// Notification reads table (tracks read status per user)
export const notificationReads = pgTable("notification_reads", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    notificationId: text("notification_id").notNull().references(() => notifications.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    readAt: timestamp("read_at").default(sql `now()`),
}, (table) => ({
    uniqueNotificationRead: unique().on(table.notificationId, table.userId),
}));
// Device tokens for push notifications
export const deviceTokens = pgTable("device_tokens", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    token: text("token").notNull().unique(),
    platform: text("platform").notNull(), // ios, android, web
    deviceId: text("device_id"),
    deviceName: text("device_name"),
    appVersion: text("app_version"),
    isActive: boolean("is_active").default(true),
    lastUsedAt: timestamp("last_used_at").default(sql `now()`),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
export const insertNotificationSchema = createInsertSchema(notifications).omit({
    id: true,
    createdAt: true,
    emailSentAt: true,
    pushSentAt: true,
    pushDeliveredAt: true,
    pushOpenedAt: true,
});
export const insertNotificationReadSchema = createInsertSchema(notificationReads).omit({
    id: true,
    readAt: true,
});
export const insertDeviceTokenSchema = createInsertSchema(deviceTokens).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    lastUsedAt: true,
});
// Invoice Template Types
export const insertInvoiceTemplateSchema = createInsertSchema(invoiceTemplates).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
// Stripe table schemas and types
// REMOVED - Stripe integration not implemented yet
// export const insertStripeCustomerSchema = createInsertSchema(stripeCustomers).omit({
//   id: true,
//   createdAt: true,
// });
// export const insertStripeSubscriptionSchema = createInsertSchema(stripeSubscriptions).omit({
//   id: true,
//   createdAt: true,
//   updatedAt: true,
// });
// export const insertStripePaymentSchema = createInsertSchema(stripePayments).omit({
//   id: true,
//   createdAt: true,
// });
// export const insertStripeInvoiceSchema = createInsertSchema(stripeInvoices).omit({
//   id: true,
//   createdAt: true,
// });
// REMOVED - Stripe integration not implemented yet
// export type StripeCustomer = typeof stripeCustomers.$inferSelect;
// export type InsertStripeCustomer = z.infer<typeof insertStripeCustomerSchema>;
// export type StripeSubscription = typeof stripeSubscriptions.$inferSelect;
// export type InsertStripeSubscription = z.infer<typeof insertStripeSubscriptionSchema>;
// export type StripePayment = typeof stripePayments.$inferSelect;
// export type InsertStripePayment = z.infer<typeof insertStripePaymentSchema>;
// export type StripeInvoice = typeof stripeInvoices.$inferSelect;
// export type InsertStripeInvoice = z.infer<typeof insertStripeInvoiceSchema>;
// Ticket History schema
export const insertTicketHistorySchema = createInsertSchema(ticketHistory).omit({
    id: true,
    createdAt: true,
});
// Weather Warnings schema
export const insertWeatherWarningSchema = createInsertSchema(weatherWarnings).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
// KI-Bildanalyse Schemas
export const insertImageAnalysisSchema = createInsertSchema(imageAnalysis).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
// Chat-Nachrichten Schema
export const insertTicketChatMessageSchema = createInsertSchema(ticketChatMessages).omit({
    id: true,
    createdAt: true,
});
// Bauzeitenplan - Gantt Chart für Bauprojekte
export const bauzeitenplan = pgTable("bauzeitenplan", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    projectId: varchar("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    gewerk: text("gewerk").notNull(), // Handwerk/Gewerk (z.B. "Rohbau", "Elektro")
    task: text("task").notNull(), // Aufgabenbeschreibung
    startDate: timestamp("start_date").notNull(),
    endDate: timestamp("end_date").notNull(),
    status: text("status").notNull().default("geplant"), // geplant, in_bearbeitung, abgeschlossen, verzögert
    priority: text("priority").notNull().default("mittel"), // niedrig, mittel, hoch
    assignedTo: text("assigned_to"), // Zugewiesene Person
    notes: text("notes"), // Zusätzliche Notizen
    color: text("color").default("blue"), // Farbe für Gantt-Balken
    sortOrder: integer("sort_order").default(0), // Reihenfolge für Drag & Drop
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
});
export const bauzeitenplanColumnWidths = pgTable("bauzeitenplan_column_widths", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    projectId: varchar("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
    gewerk: integer("gewerk").notNull().default(150),
    titel: integer("titel").notNull().default(900),
    start: integer("start").notNull().default(60),
    ende: integer("ende").notNull().default(60),
    zeitachse: integer("zeitachse").notNull().default(600),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    uniqueUserProject: unique().on(table.userId, table.projectId),
}));
export const insertBauzeitenplanSchema = createInsertSchema(bauzeitenplan).omit({
    id: true,
    userId: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    startDate: z.union([z.date(), z.string().datetime()]).transform((val) => {
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    endDate: z.union([z.date(), z.string().datetime()]).transform((val) => {
        if (typeof val === 'string')
            return new Date(val);
        return val;
    }),
    status: z.enum(["geplant", "in_bearbeitung", "abgeschlossen", "verzögert"]).default("geplant"),
    priority: z.enum(["niedrig", "mittel", "hoch"]).default("mittel"),
});
export const insertBauzeitenplanColumnWidthsSchema = createInsertSchema(bauzeitenplanColumnWidths).omit({
    id: true,
    userId: true,
    updatedAt: true
});
// Export new schemas for project members and user invitations
export const insertProjectMemberSchema = createInsertSchema(projectMembers);
export const insertProjectTeamEntrySchema = createInsertSchema(projectTeamEntries);
export const insertUserInvitationSchema = createInsertSchema(userInvitations);
// Saved Photos table for storing photos saved for later
export const savedPhotos = pgTable("saved_photos", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    organizationId: text("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
    imageUrl: text("image_url").notNull(),
    localPath: text("local_path"), // Local file path reference
    metadata: json("metadata").$type(),
    createdAt: timestamp("created_at").default(sql `now()`),
    isDeleted: boolean("is_deleted").default(false), // Soft delete flag
}, (table) => ({
    savedPhotosUserIdIdx: index("idx_saved_photos_user_id").on(table.userId),
    savedPhotosOrgIdIdx: index("idx_saved_photos_org_id").on(table.organizationId),
    savedPhotosCreatedAtIdx: index("idx_saved_photos_created_at").on(table.createdAt),
}));
// User Devices table for tracking user device information
export const userDevices = pgTable("user_devices", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    deviceFingerprint: text("device_fingerprint").notNull(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent").notNull(),
    deviceType: text("device_type").notNull(), // mobile, desktop, tablet
    operatingSystem: text("operating_system"),
    browser: text("browser"),
    screenResolution: text("screen_resolution"), // e.g., "1920x1080"
    timezone: text("timezone"), // e.g., "Europe/Berlin"
    language: text("language"),
    metadata: json("metadata").$type().default({}),
    firstSeenAt: timestamp("first_seen_at").default(sql `now()`),
    lastSeenAt: timestamp("last_seen_at").default(sql `now()`),
    accessCount: integer("access_count").default(1),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    uniqueUserDeviceFingerprint: unique().on(table.userId, table.deviceFingerprint),
    userDevicesUserIdIdx: index("idx_user_devices_user_id").on(table.userId),
    userDevicesDeviceFingerprintIdx: index("idx_user_devices_device_fingerprint").on(table.deviceFingerprint),
    userDevicesLastSeenAtIdx: index("idx_user_devices_last_seen_at").on(table.lastSeenAt),
}));
// User Access Logs table for detailed access history
export const userAccessLogs = pgTable("user_access_logs", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    deviceId: text("device_id").references(() => userDevices.id, { onDelete: "set null" }),
    ipAddress: text("ip_address"),
    accessType: text("access_type").notNull(), // login, api_call, page_view
    endpoint: text("endpoint"), // API endpoint or page path
    dataTransferred: integer("data_transferred"), // Approximate data transferred in bytes
    sessionId: text("session_id"),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    userAccessLogsUserIdIdx: index("idx_user_access_logs_user_id").on(table.userId),
    userAccessLogsDeviceIdIdx: index("idx_user_access_logs_device_id").on(table.deviceId),
    userAccessLogsCreatedAtIdx: index("idx_user_access_logs_created_at").on(table.createdAt),
    userAccessLogsAccessTypeIdx: index("idx_user_access_logs_access_type").on(table.accessType),
}));
// Insert schemas for saved photos
export const insertSavedPhotoSchema = createInsertSchema(savedPhotos).omit({
    id: true,
    createdAt: true,
});
// Insert schemas for user devices and access logs
export const insertUserDeviceSchema = createInsertSchema(userDevices).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    firstSeenAt: true,
    lastSeenAt: true,
    accessCount: true,
});
export const insertUserAccessLogSchema = createInsertSchema(userAccessLogs).omit({
    id: true,
    createdAt: true,
});
// Team Web Bug Reporting Tables
// Team Web Access - Controls which users can access the bug tracking app
export const teamWebAccess = pgTable("team_web_access", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: text("user_id").notNull().references(() => bugUsers.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("reporter"), // reporter or chaser
    isActive: boolean("is_active").default(true),
    createdBy: text("created_by").references(() => bugUsers.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    uniqueUserAccess: unique().on(table.userId),
    teamWebAccessUserIdIdx: index("idx_team_web_access_user_id").on(table.userId),
    teamWebAccessRoleIdx: index("idx_team_web_access_role").on(table.role),
}));
// Bug Reports table
export const teamBugReports = pgTable("team_bug_reports", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    title: text("title").notNull(),
    platform: text("platform").notNull(), // web, ios, android
    browser: text("browser"), // chrome, safari, firefox, edge, other (required if platform=web)
    category: text("category").notNull(), // ui_ux, functionality, crash, performance, etc.
    stepsToReproduce: text("steps_to_reproduce").notNull(),
    expectedBehavior: text("expected_behavior"),
    actualBehavior: text("actual_behavior").notNull(),
    notes: text("notes"),
    url: text("url"), // URL/Screen where bug occurred
    appVersion: text("app_version"), // App version/build
    priority: text("priority").notNull().default("medium"), // low, medium, high, critical
    reproFrequency: text("repro_frequency").notNull().default("sometimes"), // always, often, sometimes, once
    progress: text("progress").notNull().default("waiting"), // waiting, in_progress, planned_future_development, fixed, rejected
    fixedComment: text("fixed_comment"), // optional bug chaser comment when fixed
    // Auto-captured fields
    userAgent: text("user_agent"),
    os: text("os"),
    viewport: text("viewport"),
    locale: text("locale"),
    timezone: text("timezone"),
    // Audit fields
    createdBy: text("created_by").notNull().references(() => bugUsers.id, { onDelete: "cascade" }),
    updatedBy: text("updated_by").references(() => bugUsers.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
    isDeleted: boolean("is_deleted").default(false),
    deletedAt: timestamp("deleted_at"),
}, (table) => ({
    bugReportsCreatedByIdx: index("idx_bug_reports_created_by").on(table.createdBy),
    bugReportsProgressIdx: index("idx_bug_reports_progress").on(table.progress),
    bugReportsPlatformIdx: index("idx_bug_reports_platform").on(table.platform),
    bugReportsCategoryIdx: index("idx_bug_reports_category").on(table.category),
    bugReportsCreatedAtIdx: index("idx_bug_reports_created_at").on(table.createdAt),
    bugReportsIsDeletedIdx: index("idx_bug_reports_is_deleted").on(table.isDeleted),
}));
// Bug Report Attachments table
export const teamBugReportAttachments = pgTable("team_bug_report_attachments", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    bugId: text("bug_id").notNull().references(() => teamBugReports.id, { onDelete: "cascade" }),
    url: text("url").notNull(),
    filename: text("filename").notNull(),
    mimeType: text("mime_type").notNull(),
    size: integer("size").notNull(),
    uploadedBy: text("uploaded_by").notNull().references(() => bugUsers.id, { onDelete: "cascade" }),
    uploadedAt: timestamp("uploaded_at").default(sql `now()`),
}, (table) => ({
    bugAttachmentsBugIdIdx: index("idx_bug_attachments_bug_id").on(table.bugId),
    bugAttachmentsUploadedByIdx: index("idx_bug_attachments_uploaded_by").on(table.uploadedBy),
}));
// Bug Report History table - Records all changes
export const teamBugReportHistory = pgTable("team_bug_report_history", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    bugId: text("bug_id").notNull().references(() => teamBugReports.id, { onDelete: "cascade" }),
    changedBy: text("changed_by").notNull().references(() => bugUsers.id, { onDelete: "cascade" }),
    changedAt: timestamp("changed_at").default(sql `now()`),
    changeType: text("change_type").notNull(), // create, update, delete, status_change, attachment_add, attachment_delete
    fromSnapshot: json("from_snapshot").$type(),
    toSnapshot: json("to_snapshot").$type(),
    note: text("note"),
}, (table) => ({
    bugHistoryBugIdIdx: index("idx_bug_history_bug_id").on(table.bugId),
    bugHistoryChangedByIdx: index("idx_bug_history_changed_by").on(table.changedBy),
    bugHistoryChangedAtIdx: index("idx_bug_history_changed_at").on(table.changedAt),
    bugHistoryChangeTypeIdx: index("idx_bug_history_change_type").on(table.changeType),
}));
// Insert schemas
export const insertTeamWebAccessSchema = createInsertSchema(teamWebAccess).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertTeamBugReportSchema = createInsertSchema(teamBugReports).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
});
export const insertTeamBugReportAttachmentSchema = createInsertSchema(teamBugReportAttachments).omit({
    id: true,
    uploadedAt: true,
});
export const insertTeamBugReportHistorySchema = createInsertSchema(teamBugReportHistory).omit({
    id: true,
    changedAt: true,
});
// ─── Global Reels Feature ────────────────────────────────────────────────────
// Publications: images explicitly published to the global cross-tenant Reels feed
export const reelsPublications = pgTable("reels_publications", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    ticketId: text("ticket_id").notNull().references(() => tickets.id, { onDelete: "cascade" }),
    imageUrl: text("image_url").notNull(),
    imageIndex: integer("image_index").notNull().default(0),
    publishedByUserId: text("published_by_user_id").notNull().references(() => users.id),
    organizationId: text("organization_id").notNull().references(() => organizations.id),
    title: text("title"),
    description: text("description"),
    isPublished: boolean("is_published").notNull().default(true),
    createdAt: timestamp("created_at").default(sql `now()`),
    updatedAt: timestamp("updated_at").default(sql `now()`),
}, (table) => ({
    reelsPubOrgIdx: index("idx_rp_org").on(table.organizationId),
    reelsPubUserIdx: index("idx_rp_user").on(table.publishedByUserId),
    reelsPubPubIdx: index("idx_rp_pub").on(table.isPublished, table.createdAt),
    reelsPubTicketIdx: index("idx_rp_ticket").on(table.ticketId),
    reelsPubUniq: unique("uq_rp_ticket_image").on(table.ticketId, table.imageIndex),
}));
// Interactions: cross-tenant likes and saves with snapshot data
export const reelsInteractions = pgTable("reels_interactions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    publicationId: text("publication_id").notNull().references(() => reelsPublications.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id),
    organizationId: text("organization_id").references(() => organizations.id),
    type: text("type").notNull(), // 'like' | 'save'
    // Snapshot data captured at interaction time (persists after unpublish)
    imageUrl: text("image_url").notNull(),
    title: text("title"),
    publisherName: text("publisher_name"),
    publisherLogo: text("publisher_logo"),
    publisherUserId: text("publisher_user_id"),
    isSystemPublisher: boolean("is_system_publisher").notNull().default(false),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    reelsIntUserTypeIdx: index("idx_ri_user_type").on(table.userId, table.type, table.createdAt),
    reelsIntPubIdx: index("idx_ri_pub").on(table.publicationId),
    reelsIntUniq: unique("uq_ri_pub_user_type").on(table.publicationId, table.userId, table.type),
}));
// Comments: cross-tenant comments on global Reels publications
export const reelsComments = pgTable("reels_comments", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    publicationId: text("publication_id").notNull().references(() => reelsPublications.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id),
    organizationId: text("organization_id").references(() => organizations.id),
    comment: text("comment").notNull(),
    userName: text("user_name"),
    userLogo: text("user_logo"),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    reelsComPubIdx: index("idx_rc_pub").on(table.publicationId, table.createdAt),
}));
export const reelsCommentReads = pgTable("reels_comment_reads", {
    publicationId: text("publication_id").notNull().references(() => reelsPublications.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    lastReadAt: timestamp("last_read_at").notNull().default(sql `now()`),
    updatedAt: timestamp("updated_at").notNull().default(sql `now()`),
}, (table) => ({
    pk: primaryKey({ columns: [table.publicationId, table.userId], name: "pk_reels_comment_reads" }),
    reelsCommentReadsUserIdx: index("idx_rcr_user").on(table.userId, table.lastReadAt),
}));
// Follows: follow relationships between users in the global Reels context
export const reelsFollows = pgTable("reels_follows", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    followerUserId: text("follower_user_id").notNull().references(() => users.id),
    followedUserId: text("followed_user_id").notNull().references(() => users.id),
    createdAt: timestamp("created_at").default(sql `now()`),
}, (table) => ({
    reelsFollowerIdx: index("idx_rf_follower").on(table.followerUserId),
    reelsFollowedIdx: index("idx_rf_followed").on(table.followedUserId),
    reelsFollowsUniq: unique("uq_rf_follower_followed").on(table.followerUserId, table.followedUserId),
}));
// Insert schemas
export const insertReelsPublicationSchema = createInsertSchema(reelsPublications).omit({ id: true, createdAt: true, updatedAt: true });
export const insertReelsInteractionSchema = createInsertSchema(reelsInteractions).omit({ id: true, createdAt: true });
export const insertReelsCommentSchema = createInsertSchema(reelsComments).omit({ id: true, createdAt: true });
export const insertReelsCommentReadSchema = createInsertSchema(reelsCommentReads).omit({ lastReadAt: true, updatedAt: true });
export const insertReelsFollowSchema = createInsertSchema(reelsFollows).omit({ id: true, createdAt: true });
